import { createCameraCanvas, frameCameraRecording } from "../utils/cameraFraming"
import React, { useCallback, useEffect, useMemo, useRef, useState } from "react"
import { Button } from "react-bootstrap"
import { createPortal } from "react-dom"
import {
	getMediaSelectionError,
	getVideoDurationMessage,
} from "../utils/mediaUploadErrors"
import "../css/MediaPicker.css"

const isIOSDevice = () =>
	/iPad|iPhone|iPod/.test(navigator.userAgent) ||
	(navigator.platform === "MacIntel" && navigator.maxTouchPoints > 1)

const isFiniteNumber = (n) => typeof n === "number" && Number.isFinite(n)

const buildAccept = (accept, kind) => {
	if (accept) {
		if (Array.isArray(accept)) return accept.join(",")
		if (typeof accept === "string") return accept
	}
	return kind === "image" ? "image/*" : "video/*"
}

const validateVideoDuration = (file, maxDurationSec) => {
	return new Promise((resolve, reject) => {
		if (!isFiniteNumber(maxDurationSec)) return resolve()

		const url = URL.createObjectURL(file)
		const video = document.createElement("video")

		const cleanup = () => {
			try { URL.revokeObjectURL(url) } catch {}
		}

		video.preload = "metadata"
		video.onloadedmetadata = () => {
			const dur = video.duration
			cleanup()

			if (!isFiniteNumber(dur)) return resolve()
			if (dur > maxDurationSec) return reject(new Error(getVideoDurationMessage(maxDurationSec)))
			resolve()
		}
		video.onerror = () => {
			cleanup()
			resolve()
		}

		video.src = url
	})
}

const getSupportedVideoMimeType = () => {
	if (typeof MediaRecorder === "undefined") return ""

	// Prefer WebM when available. Safari 26 has a WebKit MediaRecorder
	// regression in the MP4 path that can truncate/freeze video while audio
	// continues. Older Safari versions that cannot record WebM still fall back
	// to MP4 through feature detection.
	const types = [
		"video/webm;codecs=vp8,opus",
		"video/webm;codecs=vp8",
		"video/webm",
		"video/webm;codecs=vp9,opus",
		"video/webm;codecs=vp9",
		"video/mp4",
	]

	return types.find((type) => MediaRecorder.isTypeSupported(type)) || ""
}

const makeFileName = (kind, mimeType) => {
	const stamp = new Date().toISOString().replace(/[:.]/g, "-")

	if (kind === "image") return `photo-${stamp}.jpg`

	if (mimeType.includes("mp4")) return `video-${stamp}.mp4`

	return `video-${stamp}.webm`
}

const buildCameraVideoConstraints = (captureMode) => {
	// Request sensor dimensions and let the browser orient the delivered feed.
	// Swapping dimensions for an upright phone can produce a landscape crop.
	// Never restart a recording on rotation.
	return {
		facingMode: { ideal: captureMode },
		width: { ideal: 1280 },
		height: { ideal: 720 },
		aspectRatio: { ideal: 16 / 9 },
		frameRate: { ideal: 30 },
	}
}

const applyPreferredCameraSettings = async (stream) => {
	const track = stream?.getVideoTracks?.()[0]

	if (!track?.getCapabilities || !track?.applyConstraints) return

	try {
		const capabilities = track.getCapabilities()
		const zoom = capabilities?.zoom

		if (
			zoom &&
			isFiniteNumber(zoom.min) &&
			isFiniteNumber(zoom.max) &&
			zoom.min <= 1 &&
			zoom.max >= 1
		) {
			await track.applyConstraints({ advanced: [{ zoom: 1 }] })
		}
	} catch {
		// Keep the camera usable if a device/browser rejects optional tuning.
	}
}

const CameraCaptureModal = ({
	kind,
	onClose,
	onCapture,
	onError,
	maxDurationSec,
	captureMode = "environment",
}) => {
	const framingRef = useRef(null)
	const [framed] = useState(() => window.innerWidth <= 767 && (kind === "image" || typeof HTMLCanvasElement.prototype.captureStream === "function"))
	const videoRef = useRef(null)
	const streamRef = useRef(null)
	const recorderRef = useRef(null)
	const chunksRef = useRef([])
	const discardRecordingRef = useRef(false)
	const timerRef = useRef(null)

	const [ready, setReady] = useState(false)
	const [recording, setRecording] = useState(false)
	const [recordingSeconds, setRecordingSeconds] = useState(0)
	const [confirmDiscard, setConfirmDiscard] = useState(false)

	const stopTimer = useCallback(() => {
		if (timerRef.current) {
			clearInterval(timerRef.current)
			timerRef.current = null
		}
	}, [])

	const stopStream = useCallback(() => {
		stopTimer()
		framingRef.current?.stop()
		framingRef.current = null

		if (streamRef.current) {
			streamRef.current.getTracks().forEach((track) => track.stop())
			streamRef.current = null
		}
	}, [stopTimer])

	const reportError = useCallback((msg) => {
		if (typeof onError === "function") onError(msg)
	}, [onError])

	useEffect(() => {
		let active = true

		const startCamera = async () => {
			try {
				const constraints = {
					audio: kind === "video",
					video: buildCameraVideoConstraints(captureMode),
				}

				const stream = await navigator.mediaDevices.getUserMedia(constraints)
				await applyPreferredCameraSettings(stream)

				if (!active) {
					stream.getTracks().forEach((track) => track.stop())
					return
				}

				streamRef.current = stream

				if (videoRef.current) {
					videoRef.current.srcObject = stream
					await videoRef.current.play()
				}

				setReady(true)
			} catch (e) {
				reportError("Camera access is blocked or unavailable. Check your browser camera permission and try again, or choose a file from Library.")
				onClose()
			}
		}

		startCamera()

		return () => {
			active = false
			stopStream()
		}
	}, [captureMode, kind, onClose, reportError, stopStream])

	const stopRecording = useCallback(() => {
		const recorder = recorderRef.current

		if (recorder && recorder.state !== "inactive") {
			recorder.stop()
		}
	}, [])

	const capturePhoto = () => {
		const video = videoRef.current

		if (!video || !video.videoWidth || !video.videoHeight) {
			reportError("The camera isn’t ready yet. Wait a moment and try again.")
			return
		}

		let canvas
		try {
			canvas = createCameraCanvas(video, framed).canvas
		} catch {
			reportError("We couldn’t capture the photo. Please try again.")
			return
		}

		canvas.toBlob((blob) => {
			if (!blob) {
				reportError("We couldn’t capture the photo. Try again, or choose a photo from Library.")
				return
			}

			const file = new File([blob], makeFileName("image", "image/jpeg"), {
				type: "image/jpeg",
				lastModified: Date.now(),
			})

			onCapture(file)
			onClose()
		}, "image/jpeg", 0.92)
	}

	const startRecording = () => {
		const stream = streamRef.current

		if (!stream) {
			reportError("The camera isn’t ready yet. Wait a moment and try again.")
			return
		}

		if (typeof MediaRecorder === "undefined") {
			reportError("Video recording isn’t supported in this browser. Choose a video from Library instead.")
			return
		}

		try {
			const mimeType = getSupportedVideoMimeType()
			const options = mimeType ? { mimeType } : undefined
			if (framed) framingRef.current = frameCameraRecording(videoRef.current, stream)
			const recorder = new MediaRecorder(framingRef.current?.stream || stream, options)

			chunksRef.current = []
			discardRecordingRef.current = false
			recorderRef.current = recorder

			recorder.ondataavailable = (event) => {
				if (event.data && event.data.size > 0) {
					chunksRef.current.push(event.data)
				}
			}

			recorder.onstop = () => {
				framingRef.current?.stop()
				framingRef.current = null
				stopTimer()
				setRecording(false)

				const shouldDiscard = discardRecordingRef.current
				discardRecordingRef.current = false

				if (shouldDiscard) {
					chunksRef.current = []
					onClose()
					return
				}

				const finalType = recorder.mimeType || mimeType || "video/webm"
				const blob = new Blob(chunksRef.current, { type: finalType })
				const file = new File([blob], makeFileName("video", finalType), {
					type: finalType,
					lastModified: Date.now(),
				})

				chunksRef.current = []

				onCapture(file)
				onClose()
			}

			recorder.start()
			setRecording(true)
			setRecordingSeconds(0)

			timerRef.current = setInterval(() => {
				setRecordingSeconds((current) => {
					const next = current + 1

					if (isFiniteNumber(maxDurationSec) && next >= maxDurationSec) {
						stopRecording()
					}

					return next
				})
			}, 1000)
		} catch (e) {
			framingRef.current?.stop()
			framingRef.current = null
			reportError("We couldn’t start video recording. Try again, or choose a video from Library.")
		}
	}

	const handleClose = () => {
		if (recording) {
			setConfirmDiscard(true)
			return
		}

		onClose()
	}

	const handleDiscardRecording = () => {
		discardRecordingRef.current = true
		setConfirmDiscard(false)
		stopRecording()
	}

	return createPortal(
		<div className="media-picker-camera-backdrop">
			<div className="media-picker-camera-modal">
				<div className="media-picker-camera-header">
					<div>
						<div className="media-picker-camera-title">
							{kind === "image" ? "Take photo" : "Record video"}
						</div>
						<div className="media-picker-camera-subtitle">
							{kind === "image"
								? "Captured inside the app so it is not saved to the gallery."
								: "Recorded inside the app so it is not saved to the gallery."}
						</div>
					</div>

					<Button
						type="button"
						variant="light"
						size="sm"
						className="media-picker-camera-close"
						onClick={handleClose}
					>
						×
					</Button>
				</div>

				<div className="media-picker-camera-body">
					<video
						ref={videoRef}
						className="media-picker-camera-video"
						autoPlay
						playsInline
						muted
					/>

					{!ready && (
						<div className="media-picker-camera-loading">
							Opening camera...
						</div>
					)}
				</div>

				<div className="media-picker-camera-actions">
					{kind === "image" && (
						<Button
							type="button"
							className="media-picker-btn media-picker-btn-primary"
							disabled={!ready}
							onClick={capturePhoto}
						>
							Capture photo
						</Button>
					)}

					{kind === "video" && !recording && (
						<Button
							type="button"
							className="media-picker-btn media-picker-btn-primary"
							disabled={!ready}
							onClick={startRecording}
						>
							Start recording
						</Button>
					)}

					{kind === "video" && recording && (
						<Button
							type="button"
							className="media-picker-btn media-picker-btn-danger-solid"
							onClick={stopRecording}
						>
							Stop recording
							<span className="media-picker-recording-time">
								{recordingSeconds}s
							</span>
						</Button>
					)}

					<Button
						type="button"
						className="media-picker-btn media-picker-btn-outline"
						onClick={handleClose}
					>
						Cancel
					</Button>
				</div>

				{confirmDiscard && (
					<div
						className="media-picker-discard-backdrop"
						role="dialog"
						aria-modal="true"
						aria-labelledby="media-picker-discard-title"
					>
						<div className="media-picker-discard-card">
							<div
								id="media-picker-discard-title"
								className="media-picker-discard-title"
							>
								Discard this recording?
							</div>
							<div className="media-picker-discard-text">
								This video will not be saved.
							</div>
							<div className="media-picker-discard-actions">
								<Button
									type="button"
									className="media-picker-btn media-picker-btn-outline"
									onClick={() => setConfirmDiscard(false)}
								>
									Keep recording
								</Button>
								<Button
									type="button"
									className="media-picker-btn media-picker-btn-danger-solid"
									onClick={handleDiscardRecording}
								>
									Discard
								</Button>
							</div>
						</div>
					</div>
				)}
			</div>
		</div>,
		document.body
	)
}

const MediaPicker = ({
	kind,
	accept,
	maxSizeBytes,
	maxDurationSec,
	disabled = false,
	captureLabel,
	libraryLabel,
	browseLabel = "Browse files",
	onSelect,
	onError,
	onClear,
	onRestore,
	className,
	buttonSize = "sm",
	captureMode = "environment",
	valueFile = null,
	valueUrl = null,
	removed = false,
	previewHeight = 320,
}) => {
	const captureInputRef = useRef(null)
	const libraryInputRef = useRef(null)
	const browseInputRef = useRef(null)
	const [busy, setBusy] = useState(false)
	const [objectUrl, setObjectUrl] = useState(null)
	const [cameraOpen, setCameraOpen] = useState(false)
	const [useStandardPicker, setUseStandardPicker] = useState(false)

	const isIOS = isIOSDevice()
	const isAndroid = /Android/i.test(navigator.userAgent)
	const acceptAttr = useMemo(() => buildAccept(accept, kind), [accept, kind])
	// Android can offer Camera/Camcorder/Files when given an explicit list of
	// MIME types. A broad media category gives its system photo picker a
	// better chance to open directly; handlePicked still validates formats.
	const libraryAcceptAttr = isAndroid ? (kind === "image" ? "image/*" : "video/*") : acceptAttr

	useEffect(() => {
		if (!(valueFile instanceof File)) {
			setObjectUrl(null)
			return
		}

		const url = URL.createObjectURL(valueFile)
		setObjectUrl(url)

		return () => {
			try { URL.revokeObjectURL(url) } catch {}
		}
	}, [valueFile])

	const reportError = useCallback((msg) => {
		if (typeof onError === "function") onError(msg)
	}, [onError])

	const handlePicked = useCallback(async (file) => {
		if (!file || disabled) return

		try {
			setBusy(true)

			const selectionError = getMediaSelectionError({
				file,
				kind,
				allowedTypes: Array.isArray(accept)
					? accept
					: typeof accept === "string"
						? accept.split(",").map((type) => type.trim()).filter(Boolean)
						: [],
				maxSizeBytes,
			})

			if (selectionError) {
				reportError(selectionError)
				return
			}

			if (kind === "video") {
				await validateVideoDuration(file, maxDurationSec)
			}

			if (typeof onSelect === "function") onSelect(file)
		} catch (e) {
			reportError(e?.message || "We couldn’t use this media file. Choose a different file and try again.")
		} finally {
			setBusy(false)
		}
	}, [accept, disabled, kind, maxDurationSec, maxSizeBytes, onSelect, reportError])

	const onCaptureChange = (e) => {
		const file = e.target.files?.[0] || null
		e.target.value = ""
		handlePicked(file)
	}

	const onLibraryChange = (e) => {
		const file = e.target.files?.[0] || null
		e.target.value = ""
		handlePicked(file)
	}

	const onBrowseChange = (e) => {
		const file = e.target.files?.[0] || null
		e.target.value = ""
		handlePicked(file)
	}

	const closeCamera = useCallback(() => {
		setCameraOpen(false)
	}, [])

	const openCapture = () => {
		if (disabled || busy) return

		const supportsCamera =
			!!navigator.mediaDevices?.getUserMedia &&
			(kind === "image" || typeof MediaRecorder !== "undefined")

		if (!supportsCamera) {
			reportError("In-app camera isn’t available in this browser. We’ll open your device camera instead.")
			captureInputRef.current?.click()
			return
		}

		setCameraOpen(true)
	}

	const openBrowse = async () => {
		if (disabled || busy) return
		if (useStandardPicker || typeof window.showOpenFilePicker !== "function") {
			browseInputRef.current?.click()
			return
		}
		try {
			// Keep Downloads/cloud providers accessible; validate after selection.
			const [handle] = await window.showOpenFilePicker({ multiple: false })
			if (handle) await handlePicked(await handle.getFile())
		} catch (error) {
			if (error?.name === "AbortError") return
			// A second user click preserves browser activation for the fallback.
			reportError("We couldn't open the file browser. Try Browse files again using the standard picker.")
			setUseStandardPicker(true)
		}
	}

	const handleClear = () => {
		if (disabled || busy) return
		if (typeof onClear === "function") onClear()
	}

	const handleRestore = () => {
		if (disabled || busy) return
		if (typeof onRestore === "function") onRestore()
	}

	const hasNewFile = valueFile instanceof File
	const hasExistingFile = !!valueUrl
	const mediaNoun = kind === "image" ? "photo" : "video"
	const previewSrc = removed ? null : objectUrl || valueUrl || null
	const showPreview = !!previewSrc

	const defaultCaptureLabel = kind === "image" ? "Take photo" : "Record video"
	const defaultLibraryLabel = "Choose from library"

	const captureActionLabel = showPreview
		? kind === "image"
			? hasNewFile
				? "Retake photo"
				: "Take new photo"
			: hasNewFile
				? "Record another video"
				: "Record new video"
		: captureLabel || defaultCaptureLabel

	const libraryActionLabel = libraryLabel || (isIOS ? "Choose media" : isAndroid ? defaultLibraryLabel : "Choose file")

	const clearActionLabel =
		hasNewFile && hasExistingFile
			? "Undo replacement"
			: `Remove ${mediaNoun}`

	return (
		<div className={className} style={{ "--media-picker-preview-height": `${previewHeight}px` }}>
			<input
				ref={captureInputRef}
				type="file"
				accept={acceptAttr}
				capture={captureMode}
				onChange={onCaptureChange}
				style={{ display: "none" }}
				disabled={disabled}
			/>

			<input
				ref={libraryInputRef}
				type="file"
				accept={libraryAcceptAttr}
				onChange={onLibraryChange}
				style={{ display: "none" }}
				disabled={disabled}
			/>

			<input
				ref={browseInputRef}
				type="file"
				onChange={onBrowseChange}
				style={{ display: "none" }}
				disabled={disabled}
			/>

			{showPreview && (
				<div className="media-picker-preview">
					{kind === "image" ? (
						<img
							src={previewSrc}
							alt={hasNewFile ? "New photo preview" : "Current photo"}
							className="media-picker-preview-media"
						/>
					) : (
						<video
							src={previewSrc}
							controls
							className="media-picker-preview-media"
						/>
					)}

					{hasNewFile && (
						<div className="media-picker-meta">
							<div className="media-picker-filename">{valueFile.name}</div>
							<div className="media-picker-filesize">
								{Math.max(1, Math.round(valueFile.size / 1024))} KB
							</div>
						</div>
					)}
				</div>
			)}

			{removed && (
				<div className="media-picker-removed">
					<div>
						<div className="media-picker-removed-title">
							{kind === "image" ? "Photo removed" : "Video removed"}
						</div>
						<div className="media-picker-removed-text">
							This will take effect when you save the program.
						</div>
					</div>

				<Button
					type="button"
					className="media-picker-btn media-picker-btn-outline"
					size={buttonSize}
					disabled={disabled || busy}
					onClick={handleRestore}
				>
					Undo
				</Button>
				</div>
			)}

			<div className="media-picker-buttons">
				<Button
					type="button"
					className="media-picker-btn media-picker-btn-primary"
					size={buttonSize}
					disabled={disabled || busy}
					onClick={openCapture}
				>
					{busy ? "Working..." : captureActionLabel}
				</Button>

				<Button
					type="button"
					className="media-picker-btn media-picker-btn-outline"
					size={buttonSize}
					disabled={disabled || busy}
					onClick={() => (isIOS || isAndroid ? libraryInputRef : browseInputRef).current?.click()}
				>
					{busy ? "Working..." : libraryActionLabel}
				</Button>

				{isAndroid && <Button
					type="button"
					className="media-picker-btn media-picker-btn-outline media-picker-btn-browse"
					size={buttonSize}
					disabled={disabled || busy}
					onClick={openBrowse}
				>
					{busy ? "Working..." : browseLabel}
				</Button>}

				{showPreview && typeof onClear === "function" && (
					<Button
						type="button"
						className="media-picker-btn media-picker-btn-danger"
						size={buttonSize}
						disabled={disabled || busy}
						onClick={handleClear}
					>
						{clearActionLabel}
					</Button>
				)}
			</div>

			{cameraOpen && (
				<CameraCaptureModal
					kind={kind}
					captureMode={captureMode}
					maxDurationSec={maxDurationSec}
					onClose={closeCamera}
					onCapture={handlePicked}
					onError={onError}
				/>
			)}
		</div>
	)
}

export default MediaPicker
